/**
 * End-user identity for the Observ RUM SDK (Kapitalvins parity).
 *
 * Two complementary identifiers, stamped onto every signal (spans by
 * {@link import('./otel-rum.js').UserAttributeSpanProcessor}, logs by
 * {@link import('./otel-logs.js').emitSessionEvent}) so RUM is filterable per user:
 *
 *   - `enduser.pseudo.id` — a stable, pseudonymous browser id minted on first
 *     visit and persisted in `localStorage`. Present even for anonymous visitors;
 *     it ties a visitor's sessions together WITHOUT carrying authenticated PII.
 *     Opt out entirely via {@link configureUser} `disablePseudoUser`.
 *   - `enduser.id` (+ optional `enduser.role` / `enduser.name`) — the AUTHENTICATED
 *     identity. Absent until the host calls {@link setUser} after sign-in; cleared
 *     by {@link clearUser} on sign-out.
 *
 * Leaf module: no SDK imports beyond {@link ./random-id.js}, so both the span and
 * log paths can read it without a cycle. Reads/writes are guarded — it never
 * throws, degrading to an in-memory pseudo id when `localStorage` is unusable.
 */
import { generateUuid } from './random-id.js'

/** Attribute keys (OTel `enduser.*` convention, matching the Kapitalvins SDK). */
const ATTR_ENDUSER_ID = 'enduser.id'
const ATTR_ENDUSER_ROLE = 'enduser.role'
const ATTR_ENDUSER_NAME = 'enduser.name'
const ATTR_ENDUSER_PSEUDO_ID = 'enduser.pseudo.id'

/** `localStorage` key holding the persistent pseudonymous id (cross-visit). */
export const PSEUDO_USER_STORAGE_KEY = 'observ.rum.pseudo'

/**
 * Authenticated end-user identity passed to {@link setUser}. `id` is required; the
 * label/role are optional. Numbers are accepted for `id` and stringified.
 */
export interface ObservUser {
  /** Stable application user id → `enduser.id`. */
  id: string | number
  /** Optional role/permission tag → `enduser.role`. */
  role?: string
  /** Optional display name → `enduser.name` (avoid storing raw PII if sensitive). */
  name?: string
}

/** The authenticated user, or `null` while anonymous. */
let currentUser: ObservUser | null = null
/** Whether the pseudonymous id is enabled (default on). */
let pseudoEnabled = true
/** In-memory cache of the pseudo id (also the fallback when storage is unusable). */
let pseudoId: string | undefined

/** Tune the user layer. Called once by `init`; no-op for undefined fields. */
export function configureUser(opts: { disablePseudoUser?: boolean }): void {
  if (typeof opts.disablePseudoUser === 'boolean') {
    pseudoEnabled = !opts.disablePseudoUser
  }
}

/** Attach the authenticated user to all subsequent telemetry. */
export function setUser(user: ObservUser): void {
  currentUser = user
}

/** Clear the authenticated identity (call on sign-out). The pseudo id persists. */
export function clearUser(): void {
  currentUser = null
}

/** The current authenticated user, or `null`. */
export function getUser(): ObservUser | null {
  return currentUser
}

/**
 * Resolve the persistent pseudonymous id, creating + persisting one on first use.
 * Returns `undefined` when disabled. Never throws: a blocked `localStorage`
 * (private mode / quota) degrades to an in-memory id that lasts the page's life.
 */
export function getPseudoUserId(): string | undefined {
  if (!pseudoEnabled) return undefined
  if (pseudoId) return pseudoId
  try {
    const existing = globalThis.localStorage?.getItem(PSEUDO_USER_STORAGE_KEY)
    if (existing) {
      pseudoId = existing
      return pseudoId
    }
  } catch {
    /* storage blocked → mint an in-memory id below */
  }
  pseudoId = generateUuid()
  try {
    globalThis.localStorage?.setItem(PSEUDO_USER_STORAGE_KEY, pseudoId)
  } catch {
    /* in-memory `pseudoId` stays authoritative */
  }
  return pseudoId
}

/**
 * The end-user attributes to stamp on a signal: the pseudo id (when enabled) plus
 * the authenticated `enduser.id` (+ role/name) once {@link setUser} was called.
 * Returns a fresh object each call so callers can spread it safely.
 */
export function userAttributes(): Record<string, string> {
  const attrs: Record<string, string> = {}
  const pseudo = getPseudoUserId()
  if (pseudo) attrs[ATTR_ENDUSER_PSEUDO_ID] = pseudo
  if (currentUser) {
    attrs[ATTR_ENDUSER_ID] = String(currentUser.id)
    if (currentUser.role) attrs[ATTR_ENDUSER_ROLE] = currentUser.role
    if (currentUser.name) attrs[ATTR_ENDUSER_NAME] = currentUser.name
  }
  return attrs
}

/**
 * Stamp the end-user attributes via a `setAttribute(key, value)` sink (used by the
 * span processor). Guarded so a throwing sink can never break a host request.
 */
export function applyUserAttributes(setAttribute: (key: string, value: string) => void): void {
  try {
    for (const [key, value] of Object.entries(userAttributes())) {
      setAttribute(key, value)
    }
  } catch {
    /* swallow — never let user stamping break a host span */
  }
}

/**
 * @internal Test-only. Clears the authenticated user + the in-memory pseudo cache
 * and restores the default (pseudo enabled), so config from one test can't leak.
 */
export function resetUserState(): void {
  currentUser = null
  pseudoId = undefined
  pseudoEnabled = true
}
